import React, { useState } from "react";
import { Card } from "../components/ui/card";
import { AI, apiErrorMessage } from "../lib/api";
import { Sparkles, Send, Loader2, AlertCircle, TrendingUp } from "lucide-react";
import { cn } from "../lib/utils";

/**
 * Reports · Ask AI section — larger, more prominent variant of the FAB
 * with quick-prompt chips tuned for sales analysis + a dedicated
 * "Team Insights" button that hits /api/ai/sm_insights.
 *
 * Access is already restricted by the parent Reports page (Admin / SM).
 */

const REPORT_PROMPTS = [
  "Which DSE has the highest finance sales this month?",
  "Give me the sales trend by location",
  "Which finance company has the largest share?",
  "What is the average DDA per finance sale?",
];

export default function AskAiSection({ canInsights = true }) {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(""); // "ask" | "insights"
  const [error, setError] = useState("");

  const runAsk = async (q) => {
    const text = (q ?? question).trim();
    if (!text || loading) return;
    setLoading(true); setError(""); setAnswer(""); setMode("ask");
    setQuestion(text);
    try {
      const { answer: a } = await AI.ask(text);
      setAnswer(a || "(no response)");
    } catch (e) {
      setError(apiErrorMessage(e) || "AI request failed");
    } finally {
      setLoading(false);
    }
  };

  const runInsights = async () => {
    if (loading) return;
    setLoading(true); setError(""); setAnswer(""); setMode("insights");
    try {
      const { insights } = await AI.smInsights();
      setAnswer(insights || "(no response)");
    } catch (e) {
      setError(apiErrorMessage(e) || "AI request failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-slate-200 p-5" data-testid="reports-ask-ai-section">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-2.5">
          <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
            <Sparkles className="h-4.5 w-4.5 text-primary" />
          </div>
          <div>
            <div className="font-display text-lg font-semibold text-slate-900">Ask AI</div>
            <p className="mt-0.5 text-xs text-slate-500">
              GPT 5.6 Sol · answers use only sales you can already see. Numbers cite SR numbers where possible.
            </p>
          </div>
        </div>
        {canInsights && (
          <button
            onClick={runInsights}
            disabled={loading}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md border border-primary/30 bg-primary/5 px-3 py-1.5",
              "text-xs font-semibold text-primary hover:bg-primary/10 disabled:opacity-50",
            )}
            data-testid="ask-ai-team-insights"
          >
            <TrendingUp className="h-3.5 w-3.5" />
            Team Insights
          </button>
        )}
      </div>

      {/* Quick prompts */}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {REPORT_PROMPTS.map((p) => (
          <button
            key={p}
            onClick={() => runAsk(p)}
            disabled={loading}
            className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] text-slate-700 hover:border-primary/30 hover:bg-primary/5 hover:text-primary disabled:opacity-50"
            data-testid={`ask-ai-quick-${p.slice(0, 12).replace(/\s+/g, "-").toLowerCase()}`}
          >
            {p}
          </button>
        ))}
      </div>

      {/* Input */}
      <div className="mt-3 flex items-center gap-2">
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") runAsk(); }}
          placeholder="Ask a question about your sales…"
          className="flex-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none placeholder:text-slate-400 focus:border-primary focus:ring-2 focus:ring-primary/20"
          data-testid="reports-ask-ai-input"
        />
        <button
          onClick={() => runAsk()}
          disabled={loading || !question.trim()}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white disabled:opacity-40"
          data-testid="reports-ask-ai-send"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Ask
        </button>
      </div>

      {/* Output */}
      {(answer || loading || error) && (
        <div className="mt-3">
          {loading && (
            <div className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {mode === "insights" ? "Analysing your team…" : "Thinking…"}
            </div>
          )}
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {answer && (
            <div
              className="whitespace-pre-wrap break-words rounded-md border border-slate-200 bg-slate-50/60 px-4 py-3 text-sm leading-relaxed text-slate-900"
              data-testid="reports-ask-ai-answer"
            >
              {answer}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
